// src/pages/sm/WorkPatterns.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Stack, Tooltip, Chip, Grid,
    ToggleButtonGroup, ToggleButton, Checkbox,
    Table, TableHead, TableRow, TableCell, TableBody, Paper, Fade
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    DateRange as WeeklyIcon,
    Loop as CycleIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const WorkPatterns = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [patterns, setPatterns] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPattern, setEditingPattern] = useState(null);

    const [formData, setFormData] = useState({
        pattern_code: '',
        pattern_name: '',
        pattern_type: 'WEEKLY',
        default_shift_id: '',
        weekly_off_days_json: [],
        weekly_shifts_json: {},
        cycle_length_days: 2,
        cycle_days_json: [],
        cycle_anchor_date: '',
        is_manual: 0,
        is_active: 1
    });

    const tenant = React.useMemo(() => localStorage.getItem('tenant'), []);

    const fetchPatterns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/patterns`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                setPatterns(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching patterns:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    const fetchShifts = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/shifts`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                setShifts(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching shifts:", err);
        }
    }, [tenant]);

    useEffect(() => {
        fetchPatterns();
        fetchShifts();
    }, [fetchPatterns, fetchShifts]);

    const handleOpenDialog = useCallback((pattern = null) => {
        if (pattern) {
            setEditingPattern(pattern);
            setFormData({
                pattern_code: pattern.pattern_code,
                pattern_name: pattern.pattern_name,
                pattern_type: pattern.pattern_type,
                default_shift_id: pattern.default_shift_id || '',
                weekly_off_days_json: Array.isArray(pattern.weekly_off_days_json) ? pattern.weekly_off_days_json : [],
                weekly_shifts_json: typeof pattern.weekly_shifts_json === 'object' && pattern.weekly_shifts_json !== null ? pattern.weekly_shifts_json : {},
                cycle_length_days: pattern.cycle_length_days || 2,
                cycle_days_json: Array.isArray(pattern.cycle_days_json) ? pattern.cycle_days_json : [],
                cycle_anchor_date: pattern.cycle_anchor_date ? pattern.cycle_anchor_date.split('T')[0] : '',
                is_manual: pattern.is_manual || 0,
                is_active: pattern.is_active
            });
        } else {
            setEditingPattern(null);
            setFormData({
                pattern_code: '',
                pattern_name: '',
                pattern_type: 'WEEKLY',
                default_shift_id: '',
                weekly_off_days_json: [],
                weekly_shifts_json: {},
                cycle_length_days: 2,
                cycle_days_json: [
                    { day: 1, type: 'WORK', shift_id: '' },
                    { day: 2, type: 'REST', shift_id: '' }
                ],
                cycle_anchor_date: '',
                is_manual: 0,
                is_active: 1
            });
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingPattern(null);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cycle_length_days') {
            const len = parseInt(value) || 0;
            let newCycle = [...formData.cycle_days_json];
            if (len > newCycle.length) {
                for (let i = newCycle.length + 1; i <= len; i++) {
                    newCycle.push({ day: i, type: 'WORK', shift_id: formData.default_shift_id || '' });
                }
            } else {
                newCycle = newCycle.slice(0, len);
            }
            setFormData(prev => ({ ...prev, [name]: len, cycle_days_json: newCycle }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleWeeklyOffToggle = (day) => {
        setFormData(prev => {
            const current = [...prev.weekly_off_days_json];
            const idx = current.indexOf(day);
            if (idx > -1) current.splice(idx, 1);
            else current.push(day);
            return { ...prev, weekly_off_days_json: current };
        });
    };

    const handleWeeklyShiftChange = (day, shiftId) => {
        setFormData(prev => ({
            ...prev,
            weekly_shifts_json: {
                ...prev.weekly_shifts_json,
                [day]: shiftId
            }
        }));
    };

    const handleCycleDayChange = (idx, field, value) => {
        setFormData(prev => {
            const newCycle = [...prev.cycle_days_json];
            newCycle[idx] = { ...newCycle[idx], [field]: value };
            if (field === 'type' && value === 'REST') {
                newCycle[idx].shift_id = null;
            }
            return { ...prev, cycle_days_json: newCycle };
        });
    };

    const handleSubmit = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL1}/sm/patterns${editingPattern ? `/${editingPattern.id}` : ''}`;
            const method = editingPattern ? 'put' : 'post';

            const res = await axios[method](url, formData, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });

            if (res.data.success) {
                fetchPatterns();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving pattern:", err);
            alert(err.response?.data?.message || "Error saving pattern");
        }
    }, [editingPattern, formData, fetchPatterns, handleCloseDialog, tenant]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Are you sure you want to delete this pattern?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/patterns/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                fetchPatterns();
            }
        } catch (err) {
            console.error("Error deleting pattern:", err);
            alert(err.response?.data?.message || "Error deleting pattern");
        }
    }, [fetchPatterns, tenant]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Code",
            field: "pattern_code",
            width: 150,
            cellStyle: { fontWeight: 'bold' }
        },
        { headerName: "Pattern Name", field: "pattern_name", flex: 1 },
        {
            headerName: "Type",
            field: "pattern_type",
            width: 130,
            cellRenderer: params => (
                <Chip
                    icon={params.value === 'WEEKLY' ? <WeeklyIcon /> : <CycleIcon />}
                    label={params.value}
                    size="small"
                    color={params.value === 'WEEKLY' ? "primary" : "secondary"}
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        {
            headerName: "Default Shift",
            field: "default_shift_code",
            width: 150,
            cellRenderer: params => params.data.is_manual ? <Chip label="MANUAL" size="small" color="warning" variant="filled" /> : (params.value ? <Chip label={params.value} size="small" variant="outlined" /> : '-')
        },
        {
            headerName: "Status",
            field: "is_active",
            width: 110,
            cellRenderer: params => (
                <Chip
                    label={params.value === 1 ? "Active" : "Inactive"}
                    size="small"
                    color={params.value === 1 ? "success" : "default"}
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        {
            headerName: "Actions",
            width: 120,
            pinned: 'right',
            cellRenderer: params => (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(params.data)}>
                            <EditIcon fontSize="small" color="primary" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(params.data.id)}>
                            <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ], [handleDelete, handleOpenDialog]);

    const days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

    const handleLogout = () => {
        localStorage.clear();
        setIsLoggedIn(false);
        navigate('/login');
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={handleLogout} />

            <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto', width: '100%', flexGrow: 1 }}>
                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
                                    Work Patterns
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                    Automate routine scheduling with weekly cycles or custom rotations
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    bgcolor: 'primary.main', borderRadius: 2.5, px: 3, py: 1.2, fontWeight: 700, textTransform: 'none',
                                    boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                Define New Pattern
                            </Button>
                        </Stack>

                        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' }}>
                            <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 280px)', width: '100%' }}>
                                <AgGridReact
                                    rowData={patterns}
                                    columnDefs={columnDefs}
                                    defaultColDef={{
                                        sortable: true,
                                        filter: true,
                                        resizable: true,
                                        cellStyle: { display: 'flex', alignItems: 'center', color: '#334155', fontWeight: 500 }
                                    }}
                                    pagination={true}
                                    paginationPageSize={15}
                                    animateRows={true}
                                    headerHeight={52}
                                    rowHeight={52}
                                />
                            </div>
                        </Paper>
                    </Box>
                </Fade>
            </Box>

            {/* PATTERN DIALOG */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                TransitionComponent={Fade}
                PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', p: 3, pb: 1 }}>
                    {editingPattern ? "Modify Schedule Pattern" : "Initialize Work Pattern"}
                </DialogTitle>
                <DialogContent sx={{ p: 4, pt: 1 }}>
                    <Grid container spacing={4} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Internal Code</Typography>
                            <TextField
                                fullWidth name="pattern_code" placeholder="e.g. CORE_WEEKKLY" value={formData.pattern_code} onChange={handleChange}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Designation Name</Typography>
                            <TextField
                                fullWidth name="pattern_name" placeholder="e.g. Standard Office 5/2" value={formData.pattern_name} onChange={handleChange}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#475569' }}>Logic Engine</Typography>
                            <ToggleButtonGroup
                                fullWidth color="primary" value={formData.pattern_type} exclusive
                                onChange={(e, val) => val && setFormData(prev => ({ ...prev, pattern_type: val }))}
                                sx={{ height: 45, '& .MuiToggleButton-root': { borderRadius: 2, fontWeight: 700, textTransform: 'none' } }}
                            >
                                <ToggleButton value="WEEKLY" sx={{ px: 4 }}>Standard Weekly</ToggleButton>
                                <ToggleButton value="CYCLE" sx={{ px: 4 }}>Cyclical / Rotation</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#475569' }}>Work Hours Default</Typography>
                            <TextField
                                fullWidth select name="default_shift_id" value={formData.default_shift_id} onChange={handleChange}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { borderRadius: 2, mt: 1 } } } }}
                            >
                                <MenuItem value="">Dynamic / Varies</MenuItem>
                                {shifts.map(s => (
                                    <MenuItem key={s.id} value={s.id}>{s.shift_code} - {s.shift_name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {formData.pattern_type === 'WEEKLY' && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 3, borderRadius: 4, bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#0f172a' }}>Weekly Day Mapping</Typography>
                                    
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: 11, py: 1.5 }}>DAY</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: 11, py: 1.5 }}>STATUS</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: 11, py: 1.5 }}>SHIFT OVERRIDE</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {days.map(day => {
                                                const isOff = formData.weekly_off_days_json.includes(day);
                                                const mappedShift = formData.weekly_shifts_json?.[day] || '';
                                                return (
                                                <TableRow key={day}>
                                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{day}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            size="small"
                                                            variant={isOff ? "contained" : "outlined"}
                                                            onClick={() => handleWeeklyOffToggle(day)}
                                                            sx={{ 
                                                                borderRadius: 2, fontWeight: 700, px: 2, minWidth: 80,
                                                                bgcolor: isOff ? '#ef4444' : 'transparent',
                                                                color: isOff ? '#fff' : '#64748b',
                                                                borderColor: isOff ? '#ef4444' : '#e2e8f0',
                                                                '&:hover': { bgcolor: isOff ? '#dc2626' : '#f1f5f9' }
                                                            }}
                                                        >
                                                            {isOff ? "REST" : "WORK"}
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            select fullWidth size="small" disabled={isOff} value={mappedShift}
                                                            onChange={(e) => handleWeeklyShiftChange(day, e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                        >
                                                            <MenuItem value="">Follow Default</MenuItem>
                                                            {shifts.map(s => <MenuItem key={s.id} value={s.id}>{s.shift_code}</MenuItem>)}
                                                        </TextField>
                                                    </TableCell>
                                                </TableRow>
                                            )})}
                                        </TableBody>
                                    </Table>
                                    
                                    <Typography variant="caption" sx={{ mt: 2, display: 'block', color: '#64748b', fontStyle: 'italic' }}>
                                        If "Follow Default" is selected, the day will use the Work Hours Default shift. "REST" status always overrides mapped shifts.
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}

                        {formData.pattern_type === 'CYCLE' && (
                            <Grid item xs={12}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={3} sx={{ mb: 3 }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>Sequence Length (Days)</Typography>
                                        <TextField
                                            type="number" size="small" name="cycle_length_days" value={formData.cycle_length_days} onChange={handleChange}
                                            sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                            Cycle Reference Date 
                                        </Typography>
                                        <TextField
                                            type="date" size="small" name="cycle_anchor_date" value={formData.cycle_anchor_date} onChange={handleChange}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Stack>
                                </Stack>
                                <Typography variant="caption" sx={{ mt: -1, mb: 2, display: 'block', color: '#64748b', fontStyle: 'italic' }}>
                                    The reference date specifies a fixed calendar date (e.g., a Monday) that acts as Day 1 of the cycle. If blank, Day 1 starts on the day the staff is assigned.
                                </Typography>

                                <Paper variant="outlined" sx={{ borderRadius: 3, border: '1.5px solid #f1f5f9', overflow: 'hidden' }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: 11, py: 1.5 }}>STEP</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: 11, py: 1.5 }}>CLASSIFICATION</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: 11, py: 1.5 }}>MAPPED SHIFT</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {formData.cycle_days_json.map((cd, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Day {cd.day}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            select fullWidth size="small" value={cd.type}
                                                            onChange={(e) => handleCycleDayChange(idx, 'type', e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                        >
                                                            <MenuItem value="WORK">Active Duty</MenuItem>
                                                            <MenuItem value="REST">Rest Period</MenuItem>
                                                        </TextField>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            select fullWidth size="small" disabled={cd.type === 'REST'} value={cd.shift_id || ''}
                                                            onChange={(e) => handleCycleDayChange(idx, 'shift_id', e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                        >
                                                            <MenuItem value="">Default Logic</MenuItem>
                                                            {shifts.map(s => <MenuItem key={s.id} value={s.id}>{s.shift_code}</MenuItem>)}
                                                        </TextField>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, borderRadius: 3, border: '1.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Pattern Active</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>If disabled, it won't appear in assignments.</Typography>
                                </Box>
                                <Checkbox
                                    checked={formData.is_active === 1}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                                    color="success"
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, borderRadius: 3, border: '1.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Security Mode</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Designated for manual roster override fields.</Typography>
                                </Box>
                                <Checkbox
                                    checked={formData.is_manual === 1}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_manual: e.target.checked ? 1 : 0 }))}
                                    color="warning"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontWeight: 700, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: 'primary.main', fontWeight: 700, px: 4, borderRadius: 2.5, textTransform: 'none',
                            boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`, '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        {editingPattern ? "Update Sequence" : "Initialize Pattern"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkPatterns;
